import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskLines, EASE } from "./Reveal";
import { scrollToId } from "./smooth";
import { useSettings } from "../../lib/settings";
import { useT } from "../../lib/content";
import { imgSrc } from "../../data/artworks";
import { LogoFull } from "./Logo";

const TiltFrame = ({ src, caption }) => {
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), { stiffness: 120, damping: 18 });
  const gx = useTransform(mx, [-0.5, 0.5], ["20%", "80%"]);
  const gy = useTransform(my, [-0.5, 0.5], ["15%", "85%"]);
  const glare = useTransform([gx, gy], ([x, y]) => `radial-gradient(circle at ${x} ${y}, rgba(255,240,205,0.35), transparent 55%)`);
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5); my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div className="[perspective:1400px]" onMouseMove={move} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 0 0)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 0 0)" }}
        transition={{ duration: 1.6, ease: EASE, delay: 0.35 }}
        className="relative aspect-[875/1300] w-full overflow-hidden rounded-t-full shadow-[0_50px_90px_-30px_rgba(21,32,43,0.55)]"
        data-testid="hero-featured-artwork">
        <motion.img key={src} src={imgSrc(src)} alt={caption ? `« ${caption} », œuvre de Christine P.` : "Œuvre de Christine P."}
          className="absolute inset-0 w-full h-full object-cover" initial={{ scale: 1.25 }} animate={{ scale: 1.04 }}
          transition={{ duration: 2.2, ease: EASE, delay: 0.35 }} />
        <motion.div className="absolute inset-0 pointer-events-none mix-blend-soft-light" style={{ background: glare }} />
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const { settings } = useSettings();
  const t = useT();
  const yBig = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-40 sm:pt-40 lg:pt-44 pb-16 lg:pb-24 grain">
      <div className="absolute -top-40 -right-40 w-[620px] h-[620px] rounded-full bg-[#C9DCD3]/45 blur-3xl pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7 relative z-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="text-[11px] sm:text-xs tracking-[0.32em] uppercase text-[#8A6420] font-medium mb-6 sm:mb-8">
            {t("hero_eyebrow")}
          </motion.p>
          <h1 data-testid="hero-title" className="f-display font-light text-[#15202B] leading-[0.92] tracking-[-0.02em] text-[3.6rem] sm:text-[6rem] lg:text-[7.6rem] xl:text-[8.6rem]">
            <MaskLines key={t("hero_title1") + t("hero_title2")} lines={[t("hero_title1"), <span key="i" className="italic font-normal">{t("hero_title2")}</span>]} delay={0.15} />
          </h1>
          <motion.span data-testid="hero-signature" initial={{ opacity: 0, clipPath: "inset(-30% 100% -30% -10%)" }}
            animate={{ opacity: 1, clipPath: "inset(-30% -10% -30% -10%)" }} transition={{ duration: 1.6, delay: 0.9, ease: EASE }}
            className="relative z-10 block text-[#8A6420] mt-6 sm:mt-8 ml-1 sm:ml-40"><LogoFull className="h-20 sm:h-24 lg:h-28" /></motion.span>
          <motion.p data-testid="hero-subtitle" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 1, ease: EASE }}
            className="mt-8 max-w-md text-base sm:text-lg text-[#3B4A57] font-light leading-relaxed">
            {t("hero_subtitle")}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 1, ease: EASE }}
            className="mt-10 flex flex-wrap gap-3">
            <button onClick={() => scrollToId("galerie")} data-testid="hero-explore-gallery-button" className="btn-ink group">
              {t("hero_cta1")} <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
            </button>
            <button onClick={() => scrollToId("contact")} data-testid="hero-contact-button" className="btn-line">{t("hero_cta2")}</button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div style={{ y: yBig }} className="relative w-[78%] sm:w-[62%] lg:w-[86%] mx-auto">
            <TiltFrame src={settings.hero_image} caption={settings.hero_caption} />
            {settings.hero_caption && <span className="f-sign absolute right-2 sm:-right-4 -bottom-10 text-4xl sm:text-5xl text-[#15202B] rotate-[-8deg] pointer-events-none" data-testid="hero-caption">{settings.hero_caption}</span>}
          </motion.div>
          <motion.div initial={{ opacity: 0, rotate: -40 }} animate={{ opacity: 1, rotate: 0 }} transition={{ delay: 1.4, duration: 1.2 }}
            className="absolute right-0 lg:-right-2 top-2 w-24 h-24 sm:w-28 sm:h-28 hidden sm:block">
            <svg viewBox="0 0 100 100" className="w-full h-full animate-[spin_24s_linear_infinite]">
              <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
              <text className="fill-[#15202B] text-[9.5px] tracking-[0.28em] uppercase"><textPath href="#circ">{t("hero_badge")}</textPath></text>
            </svg>
            <span className="absolute inset-0 grid place-items-center text-[#C9A15A] text-xl">✦</span>
          </motion.div>
        </div>
      </div>
      <button onClick={() => scrollToId("galerie")} aria-label="Défiler vers la galerie" data-testid="hero-scroll-indicator"
        className="hidden lg:flex absolute left-8 bottom-8 items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-[#3B4A57]">
        <ArrowDown className="w-4 h-4 animate-bounce" /> Défiler
      </button>
    </section>
  );
};
